import { RestService } from '@/core/services/rest.service';
import { AppConstants } from '@/core/utils/app.const';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { palette, updatePreset, updateSurfacePalette } from '@primeuix/themes';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { KeyOfType, presets, SurfacesType } from '../components/app.configurator';
import { LayoutConfig, LayoutService } from './layout.service';

export interface LayoutConfigCustom extends LayoutConfig {
  scale?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutServiceCustom extends LayoutService {
  override layoutConfig = signal<LayoutConfigCustom>({
    preset: 'Aura',
    primary: 'P360',
    surface: null,
    darkTheme: false,
    menuMode: 'slim-plus',
    layoutTheme: 'colorScheme',

    // CUSTOM: new variables,
    scale: environment.defaultFontSize,
  });

  protected readonly rest = inject(RestService);

  primaryColors = computed<SurfacesType[]>(() => {
    const presetPalette = presets[this.layoutConfig().preset as KeyOfType<typeof presets>].primitive;
    const colors = [
      // CUSTOM: Add custom palette for P360.
      'P360',
      'emerald',
      'green',
      'lime',
      'orange',
      'amber',
      'yellow',
      'teal',
      'cyan',
      'sky',
      'blue',
      'indigo',
      'violet',
      'purple',
      'fuchsia',
      'pink',
      'rose',
    ];
    const palettes: SurfacesType[] = [{ name: 'noir', palette: {} }];

    colors.forEach((color) => {
      if (color === 'P360') {
        palettes.push({
          name: color,
          palette: palette('#FF5722') as SurfacesType['palette'],
        });
      } else {
        palettes.push({
          name: color,
          palette: presetPalette?.[color as KeyOfType<typeof presetPalette>] as SurfacesType['palette'],
        });
      }
    });

    return palettes;
  });

  private readonly configEffect = effect(() => {
    const config = this.layoutConfig();
    if (config) {
      this.changeScale(config.scale);
    }
  });

  readonly changeScale = (value?: number) => {
    document.documentElement.style.fontSize = `${value ?? environment.defaultFontSize}px`;
  };

  // TODO: Test...
  logout = (): Observable<any> => {
    return this.rest.delete({
      url: `${AppConstants.SECURITY_HOST}/logout`,
    });
  };

  /**
   * Bring changes from the App Configurator component.
   * @param type
   * @param colorName
   * @returns
   */
  readonly updateColors = (type: string, colorName: string) => {
    const color = this.primaryColors().find((item) => item.name === colorName);
    if (!color) {
      return;
    }

    if (type === 'primary') {
      this.layoutConfig.update((state) => ({
        ...state,
        primary: color.name!,
      }));
    } else if (type === 'surface') {
      this.layoutConfig.update((state) => ({
        ...state,
        surface: color.name!,
      }));
    }
    this.applyTheme(type, color);
  };

  /**
   * Bring changes from the App Configurator component.
   * @param type
   * @param color
   */
  private readonly applyTheme = (type: string, color: any) => {
    if (type === 'primary') {
      updatePreset(this.getPresetExt(color));
    } else if (type === 'surface') {
      updateSurfacePalette(color.palette);
    }
  };

  /**
   * Bring changes from the App Configurator component.
   * @param color
   * @returns
   */
  private readonly getPresetExt = (color: SurfacesType) => {
    if (color.name === 'noir') {
      return {
        semantic: {
          primary: {
            50: '{surface.50}',
            100: '{surface.100}',
            200: '{surface.200}',
            300: '{surface.300}',
            400: '{surface.400}',
            500: '{surface.500}',
            600: '{surface.600}',
            700: '{surface.700}',
            800: '{surface.800}',
            900: '{surface.900}',
            950: '{surface.950}',
          },
          colorScheme: {
            light: {
              primary: {
                color: '{primary.950}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.800}',
                activeColor: '{primary.700}',
              },
              highlight: {
                background: '{primary.950}',
                focusBackground: '{primary.700}',
                color: '#ffffff',
                focusColor: '#ffffff',
              },
            },
            dark: {
              primary: {
                color: '{primary.50}',
                contrastColor: '{primary.950}',
                hoverColor: '{primary.200}',
                activeColor: '{primary.300}',
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.300}',
                color: '{primary.950}',
                focusColor: '{primary.950}',
              },
            },
          },
        },
      };
    } else {
      return {
        semantic: {
          primary: color.palette,
          colorScheme: {
            light: {
              primary: {
                color: '{primary.500}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.600}',
                activeColor: '{primary.700}',
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.100}',
                color: '{primary.700}',
                focusColor: '{primary.800}',
              },
            },
            dark: {
              primary: {
                color: '{primary.400}',
                contrastColor: '{surface.900}',
                hoverColor: '{primary.300}',
                activeColor: '{primary.200}',
              },
              highlight: {
                background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                color: 'rgba(255,255,255,.87)',
                focusColor: 'rgba(255,255,255,.87)',
              },
            },
          },
        },
      };
    }
  };
}
